import { useState, useRef, useEffect } from "react";
import styles from './playback-visualizer.module.css';
import playIcon from '#/assets/play.svg';
import PauseIcon from '#/assets/pause.svg?react';
import { AudioVisualizer } from './audio-visualizer';
import { useRecording } from '#/contexts/RecordingContext';
import { useAudioPlayback } from "#/contexts/PlaybackContext";
import { config } from '#/config';
import logger from "#/utils/logger";

interface PlaybackVisualizerProps {
  audioOutputFileName?: string;
  isMuted: boolean | undefined;
  overrideUrl?: string;
  onPlaybackFinish?: () => void;
}

export const PlaybackVisualizer = ({
  audioOutputFileName,
  isMuted,
  overrideUrl,
  onPlaybackFinish
}: PlaybackVisualizerProps) => {
  const [playbackStarted, setPlaybackStarted ] = useState(false);
  const [playbackEnded, setPlaybackEnded ] = useState(false);
  const { playbackObjects, replayAudio, isPlaying, isPaused, pausePlaying, resumePlaying } = useAudioPlayback();
  const prevPlayingRef = useRef(isPlaying);
  const { isRecording, cancelRecording, pauseRecording, resumeRecording } = useRecording();

  // stable refs for functions that might change identity each render
  const replayAudioRef = useRef(replayAudio);
  const pauseRecordingRef = useRef(pauseRecording);
  const resumeRecordingRef = useRef(resumeRecording);
  useEffect(() => { replayAudioRef.current = replayAudio; }, [replayAudio]);
  useEffect(() => { pauseRecordingRef.current = pauseRecording; }, [pauseRecording]);
  useEffect(() => { resumeRecordingRef.current = resumeRecording; }, [resumeRecording]);

  // prevent auto-retrigger unless overrideUrl actually changes
  const playedOnceRef = useRef(false);

  // whenever we transition from playing → stopped (and NOT paused), that's a real end
  useEffect(() => {
    if (isPlaying) {
      setPlaybackStarted(true);
    }

    if (prevPlayingRef.current && !isPlaying && !isPaused) {
      setPlaybackEnded(true);
    }
    prevPlayingRef.current = isPlaying;
  }, [isPlaying, isPaused]);

  useEffect(() => {
    // Guard clauses: only run if we have the special URL and aren't muted.
    if (!overrideUrl || isMuted) return;
    if (playedOnceRef.current) return; // Only run once for the welcome message.

    const audio = playbackObjects.current?.audioElement;
    if (!audio) return;

    // This is where you use onPlaybackFinish!
    // We define what happens when the audio ends. It simply calls the callback.
    const handleEnded = () => {
      logger.debug("PlaybackVisualizer: Audio 'ended' event fired. Calling callback.");
      onPlaybackFinish?.(); // Call the function passed from the parent.
    };

    // We attach the listener to the audio element.
    audio.addEventListener('ended', handleEnded);

    // Prepare and start the audio playback.
    logger.debug("PlaybackVisualizer: Playing overrideUrl.");
    audio.pause();
    audio.currentTime = 0;
    audio.src = overrideUrl;

    setPlaybackStarted(true);
    setPlaybackEnded(false);

    replayAudio(); // Call the function from the context to play.
    playedOnceRef.current = true;

    // The cleanup function removes the listener to prevent memory leaks.
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [overrideUrl, isMuted, replayAudio, playbackObjects, onPlaybackFinish]);

  const handleVisualizerStop = () => {
    logger.debug("Visualizer stopped; isPlaying=", isPlaying, "isPaused=", isPaused);
    if (!isPlaying && !isPaused) {
      logger.debug("→ setting playbackEnded = true");
      setPlaybackEnded(true);
    }
  };

  const handleClick = () => {
    if (playbackEnded && (audioOutputFileName || overrideUrl)) {
      // true stop → restart
      if (isRecording) cancelRecording();
      // Re-set source in case of failed fetch
      const audio = playbackObjects.current?.audioElement;
      if (audio) {
        if (overrideUrl) {
          const cacheBust = overrideUrl.includes('?') ? '&t=' : '?t=';
          audio.src = `${overrideUrl}${cacheBust}${Date.now()}`;
        } else if (audioOutputFileName) {
          audio.src = `${config.backendUrl}/audio_file?file_name=${encodeURIComponent(
            audioOutputFileName
          )}&t=${Date.now()}`;
        }
      }
      replayAudio();
      setPlaybackEnded(false);
    } else if (isPlaying) {
      // pause in-place
      pausePlaying();
      setPlaybackEnded(false);
    } else if (isPaused) {
      // resume in-place
      resumePlaying();
      pauseRecording();
      setPlaybackEnded(false);
    } else {
      // very first play
      if (audioOutputFileName) {
        pauseRecording();
        replayAudio();
      }
    }
  };

  if (isMuted) return null;

  return (
    <div
      className={`${styles.playbackContainer} ${
        isPlaying ? styles.playing : styles.stopped
      } ${playbackStarted ? styles.visible : styles.visible}`}
    >
      <button
        className={`${styles.playbackButton} ${isPlaying ? styles.buttonPause : styles.buttonPlay}`}
        onClick={handleClick}
      >
        {isPlaying ? (
          <PauseIcon className={`${styles.icon} ${styles.iconPause}`} />
        ) : (
          <img
            src={playIcon}
            className={`${styles.icon} ${styles.iconPlay}`}
          />
        )}
      </button>
      <AudioVisualizer onVisualizerStop={handleVisualizerStop} />
    </div>
  );
};